/**
 * ActionSectionBuilder Component
 * 
 * Builder for configuring dynamic action sections on the Success page.
 * Supports rich text content and action buttons that can reference
 * context_data from redemption responses (e.g., provider_url).
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Input, Select } from '@/components/common/FormField';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import type { 
  ActionSectionDefinition, 
  ActionButtonDefinition, 
  ActionButtonType,
  ActionButtonVariant 
} from '@/app/types/rewards';

import './ActionSectionBuilder.scss';

interface ActionSectionBuilderProps {
  value: ActionSectionDefinition | undefined;
  onChange: (section: ActionSectionDefinition | undefined) => void;
  title?: string;
  description?: string;
  toggleLabel?: string;
}

const BUTTON_TYPE_OPTIONS = [
  { value: 'external_link', label: 'External Link (opens in browser)' },
  { value: 'internal_navigation', label: 'Internal Navigation (within app)' },
];

const BUTTON_VARIANT_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'text', label: 'Text' },
  { value: 'accent', label: 'Accent' },
  { value: 'link', label: 'Link' },
];

const createEmptyButton = (): ActionButtonDefinition => ({
  id: `btn_${Date.now()}`,
  type: 'external_link',
  label: '',
  url_context_key: 'provider_url',
  variant: 'secondary',
});

export const ActionSectionBuilder: React.FC<ActionSectionBuilderProps> = ({
  value,
  onChange,
  title = 'Dynamic Action Section',
  description = 'Configure an optional action section with instructions and buttons. Buttons can reference URLs from the redemption response (e.g., provider_url from LoreNote).',
  toggleLabel = 'Enable Action Section',
}) => {
  const [expandedButtonIndex, setExpandedButtonIndex] = useState<number | null>(null);
  const [isEnabled, setIsEnabled] = useState(!!value);
  
  const buttons = useMemo(() => value?.buttons || [], [value]);
  const content = value?.content || '';

  // Toggle the entire action section on/off
  const handleToggleEnabled = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
    if (!enabled) {
      onChange(undefined);
    } else {
      onChange({ content: '', buttons: [] });
    }
  }, [onChange]);

  // Update content
  const handleContentChange = useCallback((newContent: string | undefined) => {
    onChange({
      content: newContent || '',
      buttons,
    });
  }, [buttons, onChange]);

  // Add a new button
  const handleAddButton = useCallback(() => {
    const newButton = createEmptyButton();
    const newButtons = [...buttons, newButton];
    onChange({
      content,
      buttons: newButtons,
    });
    setExpandedButtonIndex(newButtons.length - 1);
  }, [buttons, content, onChange]);

  // Remove a button
  const handleRemoveButton = useCallback((index: number) => {
    const newButtons = buttons.filter((_, i) => i !== index);
    onChange({
      content,
      buttons: newButtons,
    });
    setExpandedButtonIndex(null);
  }, [buttons, content, onChange]);

  // Update a button property
  const updateButton = useCallback((index: number, updates: Partial<ActionButtonDefinition>) => {
    const newButtons = buttons.map((button, i) => 
      i === index ? { ...button, ...updates } : button
    );
    onChange({
      content,
      buttons: newButtons,
    });
  }, [buttons, content, onChange]);

  return (
    <div className="action-section-builder">
      <div className="action-section-builder__header">
        <h4>{title}</h4>
        <p className="action-section-builder__description">{description}</p>
        <label className="action-section-builder__toggle">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={(e) => handleToggleEnabled(e.target.checked)}
          />
          {toggleLabel}
        </label>
      </div>

      {isEnabled && (
        <>
          {/* Rich Text Content */}
          <div className="action-section-builder__content">
            <RichTextEditor
              label="Instructions Content (optional)"
              value={content}
              onChange={(html) => handleContentChange(html || undefined)}
              placeholder="Enter instructions to display above the action buttons..."
            />
          </div>

          {/* Buttons List */}
          <div className="action-section-builder__buttons-header">
            <h5>Action Buttons</h5>
          </div>

          {buttons.length === 0 ? (
            <div className="action-section-builder__empty">
              <p>No action buttons configured. Click below to add a button.</p>
            </div>
          ) : (
            <div className="action-section-builder__list">
              {buttons.map((button, buttonIndex) => (
                <div 
                  key={buttonIndex} 
                  className={`action-section-builder__button ${expandedButtonIndex === buttonIndex ? 'expanded' : ''}`}
                >
                  {/* Button Header */}
                  <div 
                    className="action-section-builder__button-header"
                    onClick={() => setExpandedButtonIndex(expandedButtonIndex === buttonIndex ? null : buttonIndex)}
                  >
                    <div className="action-section-builder__button-summary">
                      <span className="action-section-builder__button-variant">{button.variant || 'secondary'}</span>
                      <code className="action-section-builder__button-id">{button.id || 'no-id'}</code>
                      {button.label && (
                        <span className="action-section-builder__button-label">— "{button.label}"</span>
                      )}
                      <span className="action-section-builder__button-type">
                        ({button.type === 'external_link' ? 'external' : 'internal'})
                      </span>
                    </div>
                    <div className="action-section-builder__button-actions">
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveButton(buttonIndex);
                        }}
                        title="Remove button"
                      >
                        ×
                      </button>
                      <span className="expand-icon">{expandedButtonIndex === buttonIndex ? '▼' : '▶'}</span>
                    </div>
                  </div>

                  {/* Button Editor (expanded) */}
                  {expandedButtonIndex === buttonIndex && (
                    <div className="action-section-builder__button-editor">
                      <div className="action-section-builder__button-row">
                        <Input
                          label="Button ID"
                          value={button.id}
                          onChange={(val) => updateButton(buttonIndex, { id: val })}
                          placeholder="e.g., open_lorenote"
                          hint="Unique identifier for this button"
                        />
                        <Select
                          label="Button Type"
                          value={button.type}
                          onChange={(val) => updateButton(buttonIndex, { type: val as ActionButtonType })}
                          options={BUTTON_TYPE_OPTIONS}
                        />
                      </div>

                      <Input
                        label="Button Label"
                        value={button.label}
                        onChange={(val) => updateButton(buttonIndex, { label: val })}
                        placeholder="e.g., Open My LoreNote"
                      />

                      <div className="action-section-builder__button-row">
                        <Input
                          label="URL Context Key"
                          value={button.url_context_key}
                          onChange={(val) => updateButton(buttonIndex, { url_context_key: val })}
                          placeholder="e.g., provider_url"
                          hint="Key in context_data from redemption response"
                        />
                        <Select
                          label="Button Variant"
                          value={button.variant || 'secondary'}
                          onChange={(val) => updateButton(buttonIndex, { variant: val as ActionButtonVariant })}
                          options={BUTTON_VARIANT_OPTIONS}
                        />
                      </div>

                      <Input
                        label="Static URL (optional fallback)"
                        value={button.static_url || ''}
                        onChange={(val) => updateButton(buttonIndex, { static_url: val || undefined })}
                        placeholder="e.g., https://example.com/fallback"
                        hint="Used if context_data doesn't contain the URL key"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            className="btn-add-button"
            onClick={handleAddButton}
          >
            + Add Action Button
          </button>
        </>
      )}
    </div>
  );
};

export default ActionSectionBuilder;
